<template>
  <div v-if="active" class="modal is-active">
    <div class="modal-background" />
    <!-- Modal Trapping Section -->
    <div aria-hidden="true"
         @focus="selectFirstElement()"
         tabindex="0" />
    <!-- Modal Trapping Ends -->
    <aside class="modal-card" role="modal" qa="update-profile-modal">
      <header class="title">
        <p>{{$t("updateProfile.title")}}</p>
      </header>
      <form id="user-profile-form" class="form is-flex">
        <fieldset class="image-container is-flex">
          <div class="white-circle image-center-circle is-flex cursor-pointer"
               tabindex="0"
               @keypress.enter.space="selectImage()"
               ref="first_element"
               :style="imagePreviewStyles"
               @click="selectImage()">
            <i v-if="!imageURL" class="fal fa-camera"></i>
            <div v-if="imageURL" class="grey-background is-flex">
              <i class="fal fa-camera edit-image-icon cursor-pointer" @click="removeImage"></i>
            </div>
          </div>
          <p v-if="!fileError" class="cursor-pointer no-text-mobile" @click="selectImage()">{{imageLabel}}</p>
          <p v-if="fileError" class="help has-text-danger mobile-error-text">{{fileError}}</p>
        </fieldset>
        <fieldset class="inputs-container is-flex">
          <div class="input-name is-flex">
            <label for="firstName" class="visually-hidden">$t('updateProfile.placeholders.firstName')</label>
            <input id="firstName" name="firstName" type="text" class="first-name" v-model="values.firstName" ref="firstName" maxlength="30" :placeholder="$t('updateProfile.placeholders.firstName')">
            <label for="lastName" class="visually-hidden">$t('updateProfile.placeholders.lastName')</label>
            <input id="lastName" name="lastName" type="text" class="last-name" v-model="values.lastName" ref="lastName" maxlength="30" :placeholder="$t('updateProfile.placeholders.lastName')">
          </div>
          <div class="input-help">
            <div v-if="nameError" class="help has-text-danger">
              {{$t('updateProfile.namesRequired')}}
            </div>
          </div>
          <div class="input-bio">
            <label for="introField" class="visually-hidden">Write a short summary about yourself</label>
            <textarea v-if="!isYscTeacher" name="introField" id="introField" v-model.trim="values.intro" ref="intro" :maxlength="constraints.introField" :class="{'ysc-fr' : $i18n.locale === 'fr'}" :placeholder="$t('updateProfile.placeholders.bio')"></textarea>
            <textarea v-if="isYscTeacher" name="introField" id="introField" v-model.trim="values.intro" ref="intro" :maxlength="constraints.introField" :class="{'ysc-fr' : $i18n.locale === 'fr'}" :placeholder="$t('updateProfile.placeholders.classroom')"></textarea>
            <p v-if="isWritingIntro" class="char-count">{{remainingChars}} {{$t('updateProfile.charactersLeft')}}</p>
          </div>
        </fieldset>
      </form>
      <social-handles :displayMode="'edit'" ref="socialHandles" />
      <footer class="is-flex">
        <div class="error-message-container">
          <div v-if="isError" class="help has-text-danger">{{error}}</div>
        </div>
        <div class="buttons-container is-flex">
          <button v-if="isSkipAllowed && (!this.$isYSC && !this.$isAjas && !this.$isIsef)" class="button skip-button" :class="{'disabled':!canSkip}" @click="close">
            {{$t("updateProfile.buttons.skip")}}
          </button>
          <button type="submit" form="user-profile-form" class="button save-button is-primary" :class="{'is-loading': inProgress, 'disabled' : !valid || inProgress}" @click="save">
            {{$t("updateProfile.buttons.save")}}
          </button>
        </div>
      </footer>
    </aside>
    <modal-attach :parentID="_uid" @attached="checkImage" :isUploadPermission="true" :showAvatarPicker="true"/>
    <!-- Modal Trapping Section -->
    <div aria-hidden="true"
         @focus="selectFirstElement()"
         tabindex="0" />
    <!-- Modal Trapping Ends -->
  </div>
</template>

<script>
import firebase from '@/libs/firebase'
import imageProxy from '@/libs/image-proxy'
import userAgent from '@/libs/user-agent'
import ModalAttach from '@/components/Modals/Attach'
import SocialHandles from '@/components/Controls/SocialHandles'
import componentState from '@/mixins/component-state'
import i18n from '@/i18n'
import userTypes from '@/mixins/user-types'

const states = {
  inProgress: 1,
  done: 2,
  error: 3
}

const maxSize = 10485760 // 10MB

export default {
  name: 'updateProfile',
  components: { ModalAttach, SocialHandles },
  mixins: [componentState, userTypes],
  props: ['signingUpThroughTemplate'],
  data: function () {
    return {
      active: false,
      state: 0,
      error: '',
      nameError: '',
      file: '',
      fileError: '',
      user: {},
      monitor: {},
      fileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
      hasFileToUpload: false,
      constraints: {
        introField: 300,
        fullNameMobile: 15
      },
      values: {
        avatar: '',
        firstName: '',
        lastName: '',
        intro: ''
      }
    }
  },
  computed: {
    valid: function () {
      return this.values.firstName && this.values.lastName
    },
    inProgress: function () {
      return this.state === states.inProgress
    },
    isDone: function () {
      return this.state === states.done
    },
    isError: function () {
      return this.state === states.error
    },
    imageURL: function () {
      return this.values.avatar
    },
    isIOS: function () {
      return userAgent.isIOS()
    },
    imagePreviewStyles: function () {
      const style = {}
      style.backgroundImage = `url('${this.imageURL}')`
      return style
    },
    imageLabel: function () {
      return this.imageURL ? i18n.t('updateProfile.picture.edit') : this.isYscTeacher ? i18n.t('updateProfile.picture.addSchool') : i18n.t('updateProfile.picture.add')
    },
    isWritingIntro: function () {
      return this.values.intro !== ''
    },
    remainingChars: function () {
      return this.constraints.introField - this.values.intro.length
    },
    fullName: function () {
      return `${this.values.firstName.trim()} ${this.values.lastName.trim()}`
    },
    canSkip: function () {
      if (this.$isYSC) {
        return this.values.firstName && this.values.lastName
      } else {
        return true
      }
    },
    isSkipAllowed: function () {
      return !this.$isYSC && !this.$store.getters.resourceButtonClicked
    },
    isFromJudgeRegistrationForm: function () {
      return this.$route.query.userType === this.UserTypeJudge
    },
    isYscTeacher: function () {
      return this.$isYSC && this.$store.getters.currentUser && this.$store.getters.currentUser.type === 'teacher'
    }
  },
  watch: {
    active: function (isActive) {
      isActive ? this._callback('lockScrolling', false) : this._callback('unlockScrolling', true)
    },
    canSkip: function (hasFullName) {
      if (hasFullName) {
        this.nameError = false
      }
    }
  },
  methods: {
    selectFirstElement: function () {
      this.$refs.first_element.focus()
    },
    showModal: function () {
      this.active = true
      this.fetchNamesFromStore()
    },
    checkImage: function (img) {
      if (img.file) {
        this.checkFile(img.file)
      } else {
        return this.attachImage(img.url)
      }
    },
    checkFile: function (fileToCheck) {
      if (fileToCheck) {
        if (fileToCheck.size > maxSize) {
          this.fileError = '10MB File size limit'
        } else if (this.fileTypes.indexOf(fileToCheck.type) === -1) {
          this.fileError = 'Only images allowed'
        } else {
          this.fileError = ''
          this.file = fileToCheck
          this.attachImageFile()
        }
      }
    },
    attachImage: function (imageUrl) {
      this.values.avatar = imageUrl
    },
    removeImage: function (event) {
      event.preventDefault()
      this.values.avatar = ''
      this.file = ''
      this.fileError = ''
      this.hasFileToUpload = false
    },
    attachImageFile: function () {
      this.hasFileToUpload = false
      const reader = new FileReader()

      const onSuccess = () => {
        this.attachImage(reader.result)
        this.hasFileToUpload = true
      }

      const onFail = () => {
        this.file = ''
        this.fileError = 'Unable to read image'
        this.hasFileToUpload = false
      }

      // cordova work-around; do not have addEventListener
      if (reader.addEventListener) {
        reader.addEventListener('load', onSuccess, onFail)
      } else {
        reader.onload = onSuccess
        reader.onerror = onFail
      }

      reader.readAsDataURL(this.file)
    },
    selectImage: function (source) {
      this.$store.dispatch('showAttachModal', { type: 'image', parentID: this._uid })
    },
    fetchNamesFromStore: function () {
      const names = this.$store.getters.signupNames
      if (names.length === 2) {
        if (names[0] !== '' && names[1] !== '') {
          const arrayCopy = [...names]
          this.values.firstName = arrayCopy[0]
          this.values.lastName = arrayCopy[1]
        }
      }
    },
    activateNameError: function () {
      this.nameError = true
      if (!this.values.firstName) {
        this.$refs.firstName.focus()
      } else {
        this.$refs.lastName.focus()
      }
    },
    close: function () {
      this.state = 0
      this.values = {
        avatar: '',
        firstName: '',
        lastName: '',
        intro: ''
      }
      this.active = false
      this.$emit('modalDone', true, true)
      // Temp solution
      if (this.$isGirlsAndSteam) {
        this.$router.push('/confirmation')
      }
    },
    save: function (event) {
      event.preventDefault()
      if (!this.valid) {
        this.activateNameError()
        return
      }
      this.error = ''
      this.state = states.inProgress
      this.user.userName = this.$store.getters.currentUser.userName
      this.user.uuid = this.$store.getters.currentUser.uuid
      this.upload('avatars', this.user.uuid).then((avatarURL) => {
        const proxiedImage = imageProxy.proxy(avatarURL, { width: '500', height: '500' })
        const data = {}
        data.fullName = this.fullName.trim()
        data.firstName = this.values.firstName.trim()
        data.lastName = this.values.lastName.trim()
        data.avatar = proxiedImage
        this.$store.dispatch('updateCredentials', { username: this.user.userName, data }).then((user) => {
          const profile = {}
          profile.intro = this.values.intro
          this.$store.dispatch('updateSomeProfileInfo', { username: this.user.userName, profile }).then((user) => {
            this.$refs.socialHandles.resetSocialHandle()
            this.close()
          })
        }).catch((error) => {
          this.error = error.message
          this.state = states.error
        })
      }).catch((error) => {
        this.error = error.message
        this.state = states.error
      }).catch(() => {
        console.error(`Avatar Image:: Unable to upload avatar for UUID - ${this.user.uuid}`)
        this.error = 'Unable to upload avatar'
        this.state = states.error
      })
      // Temp solution for signup confirmation pages
      if (this.$isGirlsAndSteam) {
        this.$router.push('/confirmation')
      }
      if (this.$isYSC && !this.isFromJudgeRegistrationForm && this.$store.getters.currentUser.type === this.UserTypeTeacher) {
        if (this.$store.getters.getSelectedLanguage === 'en') {
          this.$nextTick(() => {
            this.$router.push('/school-visits-2022-confirmation')
          })
        } else {
          this.$nextTick(() => {
            this.$router.push('/school-visits-2022-confirmation-fr')
          })
        }
      }
    },
    upload: function (bucketName, key) {
      return new Promise((resolve, reject) => {
        if (!this.hasFileToUpload) {
          return resolve(this.imageURL)
        }

        firebase.projectBoard.signIn().then(() => {
          this.monitor = { task: null, progress: 0, done: false, error: false, uploading: false }
          const bucket = firebase.storage().ref(bucketName)
          this.monitor.task = bucket.child(`${key}`).putString(this.imageURL, 'data_url')
          this.monitorUpload(resolve, reject)
        }).catch((error) => {
          console.error(`Avatar Image:: Unable to sign in to firebase - ${error}`)
          reject(error)
        })
      })
    },
    monitorUpload: function (resolve, reject) {
      this.monitor.task.on('state_changed', (snapshot) => {
        this.monitor.uploading = snapshot.state === firebase.storage.TaskState.RUNNING
        this.monitor.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      }, (error) => {
        this.monitor.task = null
        this.monitor.error = true
        this.monitor.uploading = false
        reject(error)
      }, async () => {
        const URL = await this.monitor.task.snapshot.ref.getDownloadURL()
        resolve(URL)
        this.monitor.task = null
        this.monitor.done = true
        this.monitor.uploading = false
        this.hasFileToUpload = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  $border-color: rgb(190, 190, 190);
  $modal-body-color: #F2F2F2;
  $error-message-height: 2.5rem;

  .modal {
    z-index: 50;
  }
  .modal-card {
    background: $modal-body-color;
    text-align: center;
  }

  // HEADER
  .title {
    border-bottom: 1px solid $border-color;
    margin-bottom: 2.5rem;
    p {
      padding: 1.8rem 0 1.4rem;
      font-weight: $primary-font-weight;
      font-family: $primary-font;
      font-size: 1.5rem;
    }
  }

  // GENERAL FORM LAYOUT
  .form {
    max-width: 500px;
    margin: 0 auto;
    width: 90%;
    align-items: center;
  }
  .image-container {
    width: 40%;
    flex-shrink: 0;
    flex-direction: column;
    margin-right: 1rem;
    p {
      margin-top: 0.8rem;
      font-size: 0.9rem;
      font-weight: 500;
    }
  }
  .inputs-container {
    flex-shrink: 0;
    width: 60%;
    flex-direction: column;
  }

  // IMAGE SECTION
  .white-circle {
    width: 145px;
    height: 145px;
    background: white;
    border-radius: 50%;
    margin: 0 auto;
    justify-content: center;
    align-items: center;
    box-shadow: 1px 4px 6px #93939377;
    transition: 0.5s;
      i {
        font-size: 3rem;
      }
      &:hover {
        transition: 0.5s;
        box-shadow: 1px 4px 10px darken(#93939377, 20%);
      }
  }
  .white-circle:hover {
    .edit-image-icon {
      transition: 0.2s;
      opacity: 1;
    }
  }
  .edit-image-icon {
    opacity: 0;
    transition: all 0.2s;
    color: white;
  }
  .grey-background {
    background: #1a1a1a42;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: all 0.7s;
    &:hover {
      transition: 0.7s;
      opacity: 1;
    }
  }
  .image-center-circle {
    background-size: cover;
    background-position: center;
  }

  // TEXT INPUTS SECTION
  input {
    border: none;
    box-shadow: none;
    border-radius: 0.2rem;
    height: 40px;
    font-size: 0.85rem;
    transition: 0.1s;
    &:focus {
      border:1px solid lighten($primary, 10%);
      box-shadow: 0 0 0 0.125em lighten($primary, 40%);
      transition: 0.1s;
    }
  }
  .input-name {
    justify-content: space-between;
  }
  .first-name,
  .last-name {
    width: 48%;
    padding-left: 1rem;
  }
  .input-help {
    min-height: 1rem;
    width: 100%;
    display: flex;
    .help {
      margin: 0.3rem 0;
      width: 100%;
    }
  }
  .input-bio {
    position: relative;
  }
  textarea {
    resize: none;
    border: none;
    box-shadow: none;
    border-radius: 0.2rem;
    width: 100%;
    height: 11.2rem;
    display: block;
    padding: 1rem 1rem;
    line-height: 1.8;
    font-size: 1rem;
    transition: 0.1s;
    &:focus {
      border:1px solid lighten($primary, 10%);
      box-shadow: 0 0 0 0.125em lighten($primary, 40%);
      transition: 0.1s;
    }
    &.ysc-fr {
      height: 14rem;
      padding: 0.8rem 1rem;
    }
  }
  ::placeholder,
  ::-webkit-input-placeholder {
    font-size: 0.85rem;
  }
  .char-count {
    font-size: 0.7rem;
    color: $grey-light;
    position: absolute;
    right: 0;
  }

  // FOOTER
  footer {
    flex-direction: column;
    padding-bottom: 3rem;
  }
  .error-message-container {
    height: $error-message-height;
    width: 100%;
  }
  .buttons-container {
    margin-left: 4rem;
    align-items: flex-start;
    justify-content: left;
  }
  .button {
    flex: 0 0 auto;
    border-radius: 0;
    border: .1rem solid $primary;
    height: auto;
    padding: .3rem 1.8rem;
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
  }
  .skip-button {
    color: $primary;
    background: none;
    font-weight: 600;
    padding: .3rem 1.6rem;
    margin-right: 1rem;
    transition: 0.4s;
    &.disabled {
      cursor: not-allowed;
      color: #cacaca;
      border-color: #cacaca;
    }
    &:hover:not(.disabled) {
      transition: 0.4s;
      background: $primary;
      color: white;
    }
  }
  .save-button {
    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  // RESPONSIVE
  @include mobile {
    // GENERAL LAYOUT
    .modal-card {
      max-width: 98vw;
    }
    form {
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    .image-container,
    .inputs-container {
      width: 100%;
    }

    // HEADER
    .title {
      margin-bottom: 1rem;
      p {
        padding: 1.3rem 0 1.1rem;
      }
    }

    // IMAGE SECTION
    .image-container {
      margin-bottom: 1rem;
      margin-right: 0;
    }
    .white-circle {
      height: 130px;
      width: 130px;
    }

    .no-text-mobile {
      display: none;
    }

    .mobile-error-text {
      font-size: 0.4rem;
    }

    // INPUT SECTION
    textarea {
      font-size: 1rem;
      height: 8rem;
      line-height: 1.5;
      &.ysc-fr {
        height: 10rem;
        line-height: 1.4;
      }
    }
    ::placeholder,
    ::-webkit-input-placeholder {
      font-size: 1rem;
    }
    input {
      font-size: 1rem
    }

    // FOOTER
    footer {
      padding-bottom: 2rem;
    }
    .error-message-container {
      height: 1.8rem;
    }
  }
  @media (max-width: 484px) {
    textarea {
      &.ysc-fr {
        height: 11.5rem;
      }
    }
  }
  @include mini-mobile {
    .white-circle {
      height: 120px;
      width: 120px;
    }
    textarea {
      height: 10rem;
      &.ysc-fr {
        height: 12rem;
      }
    }
  }
  @media (max-width: 413px) {
    textarea {
      &.ysc-fr {
        height: 13rem;
      }
    }
  }

  @media (max-width: 375px) {
    textarea {
      height: 11rem;
    }
  }

  @media (max-height: 550px) and (max-width: 400px) {
    .modal-card {
      height: 100vh;
      max-height: 100vh;
    }
    header {
      margin-bottom: 0.8rem;
    }
    .mobile-error-text {
      margin-top: 0.5rem;
    }
    // input {
    //   height: 35px;
    // }
    textarea {
      padding: 0.7rem 1rem;
    }
    footer {
      padding-bottom: 0.8rem;
      background: $modal-body-color;
    }
    .error-message-container {
      height: 1.5rem;
      width: 100%;
    }
  }
</style>
